/* Created by Interactive 63 - https://interactive63.com */
/**
 * Actions for editing animated properties from the inspector: the keyframe diamond, value edits
 * with the auto-key rules, keyframe time changes and scene duration changes. Every action that
 * touches several elements or keyframes is a single undo step.
 */
import { sampleTrack } from '../../core/timeline/sample';
import { cycleTime } from '../../core/timeline/repeat';
import {
  canAddProperty,
  findKeyframe,
  findTrack,
  keyframesAt,
  moveKeyframes,
  removeKeyframes,
  scaleKeyframeTimes,
  setKeyframe,
} from '../../core/timeline/keyframes';
import type { ElementId, PropertyKey, PropertyValue, Track } from '../../core/model/types';
import { setStaticValue } from '../../core/properties/static-paint';
import type { Store } from './store';

export const MIN_DURATION_MS = 100;
export const MAX_DURATION_MS = 120000;

/** Supplies the value an element shows for a property at the playhead. */
export type ValueReader = (elementId: ElementId, property: PropertyKey) => PropertyValue;

export type EditOutcome = 'applied' | 'rejected' | 'unavailable';

/** Maps a scene time into the first cycle for a repeating track; other tracks keep the time. */
function localTime(track: Track | undefined, at: number): number {
  return track?.repeat ? Math.round(cycleTime(track, at).time) : at;
}

/** Whether a keyframe sits at the given time on an element's track. */
function hasKeyframeAt(
  store: Store,
  elementId: ElementId,
  property: PropertyKey,
  time: number,
): boolean {
  const { project } = store.getState();
  const track = findTrack(project, elementId, property);
  if (!track) return false;
  return keyframesAt(project, elementId, time).some((kf) => track.keyframes.includes(kf));
}

/**
 * The diamond: removes the keyframe at the playhead when every element has one, otherwise adds
 * a keyframe holding the element's current value to each element that lacks one.
 */
export function toggleKeyframeAtPlayhead(
  store: Store,
  elementIds: readonly ElementId[],
  property: PropertyKey,
  time: number,
  readValue: ValueReader,
): void {
  const at = Math.round(time);
  const allKeyed = elementIds.every((id) => hasKeyframeAt(store, id, property, at));
  store.updateProject(allKeyed ? 'Remove keyframe' : 'Add keyframe', (draft) => {
    for (const id of elementIds) {
      const track = findTrack(draft, id, property);
      if (allKeyed) {
        const local = localTime(track, at);
        const ids =
          track?.keyframes.filter((kf) => Math.abs(kf.time - local) <= 0.5).map((kf) => kf.id) ??
          [];
        removeKeyframes(draft, ids);
      } else if (!hasKeyframeAt(store, id, property, at) && canAddProperty(draft, id, property)) {
        setKeyframe(draft, id, property, localTime(track, at), readValue(id, property));
      }
    }
  });
}

/**
 * Applies an edited value. With no track the edit is a static change: unanimated gradient stop
 * colours are rewritten in the artwork, anything else becomes a single keyframe at the playhead. With a track and auto-key on it creates or updates the keyframe at the playhead.
 * With a track and auto-key off nothing changes for that element and `rejected` is returned.
 */
export function setPropertyValue(
  store: Store,
  elementIds: readonly ElementId[],
  property: PropertyKey,
  value: PropertyValue | ((elementId: ElementId) => PropertyValue),
  options: { time: number; coalesceKey?: string; onMappedToCycle?: () => void },
): EditOutcome {
  const { autoKey } = store.getState().editor;
  const at = Math.round(options.time);
  const result = { rejected: false, applied: false, mapped: false };

  store.updateProject(
    'Edit value',
    (draft) => {
      for (const id of elementIds) {
        if (!canAddProperty(draft, id, property)) continue;
        const next = typeof value === 'function' ? value(id) : value;
        const track = findTrack(draft, id, property);
        // An unanimated stop colour or text setting is part of the artwork, so it is edited in place.
        if (!track?.keyframes.length && setStaticValue(draft, id, property, next)) {
          result.applied = true;
          continue;
        }
        if (track && track.keyframes.length > 0 && !autoKey) {
          result.rejected = true;
          continue;
        }
        const local = localTime(track, at);
        if (local !== at) result.mapped = true;
        setKeyframe(draft, id, property, local, next);
        result.applied = true;
      }
    },
    { coalesceKey: options.coalesceKey },
  );
  if (result.mapped) options.onMappedToCycle?.();
  if (result.rejected && !result.applied) return 'rejected';
  return result.applied ? 'applied' : 'unavailable';
}

/** Moves one keyframe to a new time, following the same collision rules as dragging. */
export function setKeyframeTime(store: Store, keyframeId: string, time: number): void {
  const found = findKeyframe(store.getState().project, keyframeId);
  if (!found) return;
  const delta = Math.round(time) - found.keyframe.time;
  if (delta === 0) return;
  store.updateProject('Change keyframe time', (draft) => {
    moveKeyframes(draft, [keyframeId], delta);
  });
}

/** Adds a keyframe at the playhead on every existing track of the given elements. */
export function keyAllAtPlayhead(
  store: Store,
  elementIds: readonly ElementId[],
  time: number,
): void {
  const at = Math.round(time);
  const ids = new Set(elementIds);
  store.updateProject('Add keyframes', (draft) => {
    for (const track of draft.tracks.filter((t) => ids.has(t.elementId) && t.enabled)) {
      const value = sampleTrack(track, at, draft.scene.duration);
      if (value !== undefined) {
        setKeyframe(draft, track.elementId, track.property, localTime(track, at), value);
      }
    }
  });
}

/** How many keyframes lie after a candidate end time. */
export function keyframesBeyond(store: Store, duration: number): number {
  return store
    .getState()
    .project.tracks.reduce(
      (sum, t) => sum + t.keyframes.filter((kf) => kf.time > duration).length,
      0,
    );
}

/**
 * Changes the scene duration. When scaling, keyframe times scale with it. Otherwise keyframes
 * beyond the new end are removed, after `confirmRemoval` agrees. Returns whether it applied.
 */
export function setDuration(
  store: Store,
  ms: number,
  options: { scale: boolean; confirmRemoval: (count: number) => boolean },
): boolean {
  const duration = Math.min(MAX_DURATION_MS, Math.max(MIN_DURATION_MS, Math.round(ms)));
  const previous = store.getState().project.scene.duration;
  if (duration === previous) return false;

  if (!options.scale) {
    const lost = keyframesBeyond(store, duration);
    if (lost > 0 && !options.confirmRemoval(lost)) return false;
  }

  store.transaction('Change duration', () => {
    store.updateProject('Change duration', (draft) => {
      if (options.scale) {
        scaleKeyframeTimes(draft, duration / previous);
      } else {
        const beyond = draft.tracks.flatMap((t) =>
          t.keyframes.filter((kf) => kf.time > duration).map((kf) => kf.id),
        );
        removeKeyframes(draft, beyond);
      }
      draft.scene.duration = duration;
    });
    store.updateEditor((draft) => {
      draft.playhead = Math.min(draft.playhead, duration);
    });
  });
  return true;
}

/**
 * Keys stroke reveal on: from nothing visible at the playhead to fully drawn one second later,
 * or at the scene end if that comes first.
 */
export function drawOn(store: Store, elementIds: readonly ElementId[], time: number): void {
  const duration = store.getState().project.scene.duration;
  const end = Math.min(Math.round(time) + 1000, duration);
  const start = Math.max(0, Math.min(Math.round(time), end - 1));
  store.updateProject('Draw on', (draft) => {
    for (const id of elementIds) {
      if (!canAddProperty(draft, id, 'draw')) continue;
      setKeyframe(draft, id, 'draw', start, { start: 0, end: 0 });
      setKeyframe(draft, id, 'draw', end, { start: 0, end: 100 });
    }
  });
}
